import React from 'react';

// Shared "did you mean…" renderer for every ticker-entry surface (backtest
// runner, strategy builder, markets + nav lookups). Given a `resolve_symbol`
// response from the backend, it shows one-click correction chips when the typed
// symbol is invalid but we have a suggestion — a colloquial alias ("dow" → ^DJI),
// a wrong-asset-class fix (XAUUSD picked as equity → Forex), or a fuzzy match for
// a typo. Clicking a chip calls `onApply(symbol, assetType)`; the parent applies
// it however it likes (set form state, or navigate).
//
// Renders nothing when the symbol is valid, still loading, or has no suggestion,
// so it's safe to always mount below a symbol field.

const ASSET_LABEL = { stock: 'Stock', fx: 'Forex', crypto: 'Crypto' };

export default function SymbolSuggestion({ result, onApply, compact = false }) {
  if (!result || result.valid) return null;

  const { suggested_symbol, suggested_asset_type, suggestion_reason } = result;
  const suggestions = Array.isArray(result.suggestions) ? result.suggestions : [];

  // Build a de-duped chip list: the single best suggestion first, then the rest.
  const chips = [];
  const seen = new Set();
  const push = (symbol, assetType, quoteType, name) => {
    if (!symbol) return;
    const key = `${symbol}:${assetType}`;
    if (seen.has(key)) return;
    seen.add(key);
    chips.push({ symbol, assetType, quoteType, name });
  };
  if (suggested_symbol) push(suggested_symbol, suggested_asset_type, '', '');
  suggestions.forEach((s) => push(s.symbol, s.asset_type, s.quote_type, s.name));
  if (!chips.length) return null;

  let lead;
  if (suggestion_reason === 'ambiguous') {
    lead = `${result.symbol} trades in more than one market — which?`;
  } else if (suggestion_reason === 'asset_class') {
    lead = `${suggested_symbol} looks like a ${ASSET_LABEL[suggested_asset_type] || suggested_asset_type} symbol —`;
  } else if (suggestion_reason === 'alias') {
    lead = 'Did you mean:';
  } else {
    lead = 'No exact match — did you mean:';
  }

  return (
    <div
      style={{
        display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 6,
        marginTop: 6, fontSize: compact ? 11 : 12, color: 'var(--muted)',
      }}
    >
      <span>{lead}</span>
      {chips.map((c) => {
        const tag = c.quoteType || (c.assetType && c.assetType !== 'stock' ? ASSET_LABEL[c.assetType] : '');
        return (
          <button
            key={`${c.symbol}:${c.assetType}`}
            type="button"
            onClick={() => onApply(c.symbol, c.assetType)}
            title={c.name ? `${c.symbol} — ${c.name}` : `Use ${c.symbol}`}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 5,
              padding: '3px 9px', borderRadius: 14, cursor: 'pointer',
              border: '1px solid var(--accent, #6366f1)',
              background: 'transparent', color: 'var(--text)',
              fontSize: compact ? 11 : 12, fontWeight: 600, lineHeight: 1.4,
            }}
          >
            <span>{c.symbol}</span>
            {tag && (
              <span style={{ color: 'var(--muted)', fontWeight: 400, fontSize: '0.9em' }}>{tag}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}
